/**
 * Binary DICOM retrieval from connector-next.
 *
 * Fetches the `.dcm` route directly rather than through the generated client:
 * `connectorNextFetch` treats HTTP 202 (transfer still in flight) as success
 * and discards the status code, and 200/202/404 must be told apart here.
 *
 * PHI: DICOM UIDs never reach logs or file names; errors carry only counts
 * and HTTP statuses, and files are named by ordinal because a file name
 * reaches parser error toasts. Requests are same-origin, matching
 * `connector-next-fetcher`'s empty base URL.
 */

import {
    abortError,
    isAbortError,
    RetryNeededError,
    RetryTimeoutError,
    withRetryOn202,
} from '../retryOn202';

export { isAbortError };

/** One SOP instance, fully addressed for the `.dcm` route. */
export type InstanceAddress = {
    studyInstanceUid: string;
    seriesInstanceUid: string;
    sopInstanceUid: string;
};

/** The connector has no such instance (404); retrying will not help. */
export class InstanceNotFoundError extends Error {
    public constructor() {
        super('Instance not found');
        this.name = 'InstanceNotFoundError';
    }
}

/**
 * A transport failure (server error, network failure, bad media type) stopped
 * the download. Carries no detail: never UIDs or URLs.
 */
export class DownloadFailedError extends Error {
    public constructor() {
        super('Instance download failed');
        this.name = 'DownloadFailedError';
    }
}

/**
 * A single instance exceeded its per-request deadline. Skippable, like an
 * incomplete transfer: one slow instance must not fail the whole open.
 */
export class RequestDeadlineError extends Error {
    public constructor() {
        super('Request deadline exceeded');
        this.name = 'RequestDeadlineError';
    }
}

/** Per-request transport deadline; expiry skips the instance, not an abort. */
const REQUEST_DEADLINE_MS = 60_000;

/** Media types accepted for instance bytes (openapi + generic binary). */
const DICOM_MEDIA_TYPES = ['application/dicom', 'application/octet-stream'];

/**
 * Fetches a single SOP instance's DICOM bytes.
 *
 * - 200 with a DICOM media type → the `.dcm` bytes as a `Blob`.
 * - 200 with any other media type → error; a proxy or SPA-fallback body must
 *   never reach the DICOM parser.
 * - 202 → throws {@link RetryNeededError} (wrap in `withRetryOn202` to poll).
 * - 404 → throws {@link InstanceNotFoundError}.
 * - deadline expiry → {@link RequestDeadlineError}; only a caller abort
 *   escapes as an AbortError.
 */
export function fetchInstanceBlob(
    address: InstanceAddress,
    signal?: AbortSignal
): Promise<Blob> {
    return withRequestDeadline(
        signal,
        REQUEST_DEADLINE_MS,
        async (deadline) => {
            const response = await window.fetch(instanceUrl(address), {
                signal: deadline,
                headers: { Accept: 'application/dicom' },
            });
            if (response.status === 200) {
                assertDicomMediaType(response);
                return await response.blob();
            }
            if (response.status === 202) {
                throw new RetryNeededError();
            }
            if (response.status === 404) {
                throw new InstanceNotFoundError();
            }
            throw new Error(`Instance request failed (${response.status})`);
        }
    );
}

/**
 * Runs one request under a transport deadline, so a server that accepts a
 * request and then never answers it cannot hold a caller open indefinitely.
 *
 * The deadline is composed manually rather than with `AbortSignal.any` so its
 * expiry stays distinguishable from a caller abort: expiry surfaces as
 * {@link RequestDeadlineError}, which callers treat as this request failing,
 * while a caller's own abort escapes as an AbortError and stops the work.
 *
 * @param signal The caller's cancellation, if any.
 * @param deadlineMs How long the request may take before it is abandoned.
 * @param request Runs the request; must pass the signal it is given to the
 * transport, or the deadline cannot reach it.
 */
export async function withRequestDeadline<T>(
    signal: AbortSignal | undefined,
    deadlineMs: number,
    request: (deadline: AbortSignal) => Promise<T>
): Promise<T> {
    throwIfAborted(signal);
    const deadline = new AbortController();
    const onAbort = (): void => deadline.abort();
    const timer = setTimeout(() => deadline.abort(), deadlineMs);
    signal?.addEventListener('abort', onAbort, { once: true });

    try {
        return await request(deadline.signal);
    } catch (error) {
        if (isAbortError(error) && !signal?.aborted) {
            throw new RequestDeadlineError();
        }
        throw error;
    } finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', onAbort);
    }
}

/** Media types accepted for series-zip bytes (openapi + generic binary). */
const ZIP_MEDIA_TYPES = ['application/zip', 'application/octet-stream'];

/**
 * Fetches a whole series as a ZIP.
 *
 * Same status contract as {@link fetchInstanceBlob}:
 * - 200 with a ZIP media type → the bytes as a `Blob`.
 * - 200 with any other media type → error; a proxy or SPA-fallback body must
 *   never reach the DICOM parser.
 * - 202 → throws {@link RetryNeededError} (wrap in `withRetryOn202` to poll).
 * - 404 → throws {@link InstanceNotFoundError}.
 * - deadline expiry → {@link RequestDeadlineError}; only a caller abort
 *   escapes as an AbortError.
 *
 * @param studyInstanceUid - Study of the series.
 * @param seriesInstanceUid - Series to fetch.
 * @param signal - Caller abort.
 */
export async function fetchSeriesZipBlob(
    studyInstanceUid: string,
    seriesInstanceUid: string,
    signal?: AbortSignal
): Promise<Blob> {
    throwIfAborted(signal);
    // Deadline composed manually (not AbortSignal.any) so its expiry is
    // distinguishable from a caller abort.
    const deadline = new AbortController();
    const onAbort = (): void => deadline.abort();
    const timer = setTimeout(() => deadline.abort(), REQUEST_DEADLINE_MS);
    signal?.addEventListener('abort', onAbort, { once: true });

    try {
        const study = encodeURIComponent(studyInstanceUid);
        const series = encodeURIComponent(seriesInstanceUid);
        const response = await window.fetch(
            `/api/studies/${study}/series/${series}.zip`,
            { signal: deadline.signal, headers: { Accept: 'application/zip' } }
        );
        if (response.status === 200) {
            assertMediaType(response, ZIP_MEDIA_TYPES, 'series');
            return await response.blob();
        }
        if (response.status === 202) {
            throw new RetryNeededError();
        }
        if (response.status === 404) {
            throw new InstanceNotFoundError();
        }
        throw new Error(`Series request failed (${response.status})`);
    } catch (error) {
        if (isAbortError(error) && !signal?.aborted) {
            throw new RequestDeadlineError();
        }
        throw error;
    } finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', onAbort);
    }
}

export type DownloadInstancesOptions = {
    /** Max instances fetched at once. */
    concurrency?: number;
    signal?: AbortSignal;
    /** Called after each instance settles (downloaded or skipped). */
    onProgress?: (settled: number, total: number) => void;
    /** Whole-download deadline; defaults to {@link DOWNLOAD_DEADLINE_MS}. */
    deadlineMs?: number;
};

export type DownloadInstancesResult = {
    /** One `.dcm` File per instance whose bytes were retrieved. */
    files: File[];
    /**
     * Instances whose bytes were not retrieved (absent, never completed, or
     * cut off by a deadline), in request order.
     */
    skipped: InstanceAddress[];
};

const DEFAULT_CONCURRENCY = 6;

/**
 * Deadline for the whole download. Without it a patient whose transfer never
 * starts answers 202 for every instance, and the pool polls for
 * `instances / concurrency × 10 min` before reporting anything.
 */
export const DOWNLOAD_DEADLINE_MS = 20 * 60 * 1_000; // 20 minutes

/**
 * Downloads every instance to a `File`, polling those still transferring.
 *
 * Skippable failures (absent, never completed, deadline expiry) are reported
 * in `skipped` so a partially-synced patient still opens; the caller decides
 * whether the shortfall is acceptable. Any other failure cancels the rest
 * and rejects with {@link DownloadFailedError}; a caller abort rejects with
 * an AbortError. A rolling worker pool, so one slow instance occupies one
 * slot instead of stalling a batch.
 */
export async function downloadInstancesToFiles(
    instances: readonly InstanceAddress[],
    options: DownloadInstancesOptions = {}
): Promise<DownloadInstancesResult> {
    const concurrency = options.concurrency ?? DEFAULT_CONCURRENCY;
    const deadlineMs = options.deadlineMs ?? DOWNLOAD_DEADLINE_MS;
    const { signal, onProgress } = options;
    const total = instances.length;

    // Internal controller so one worker's hard failure cancels the others;
    // a caller abort and the whole-download deadline both forward into it.
    const controller = new AbortController();
    const onCallerAbort = (): void => controller.abort();
    if (signal?.aborted) {
        controller.abort();
    }
    signal?.addEventListener('abort', onCallerAbort, { once: true });
    const deadlineTimer = setTimeout(() => controller.abort(), deadlineMs);

    // Indexed by request order, so an instance the pool never reached stays
    // null and is reported as skipped rather than dropped from both lists.
    const blobs = new Array<Blob | null>(total).fill(null);
    let failed = false;
    let next = 0;
    let settled = 0;

    const worker = async (): Promise<void> => {
        // `next++` claims a distinct index synchronously before the first
        // await, so no instance is fetched twice.
        while (next < total && !controller.signal.aborted) {
            const index = next++;
            try {
                blobs[index] = await downloadOne(
                    instances[index],
                    controller.signal
                );
            } catch (error) {
                if (isAbortError(error)) {
                    return;
                }
                failed = true;
                controller.abort();
                return;
            }
            settled += 1;
            onProgress?.(settled, total);
        }
    };

    try {
        await Promise.all(
            Array.from({ length: Math.min(concurrency, total) }, () => worker())
        );
    } finally {
        clearTimeout(deadlineTimer);
        signal?.removeEventListener('abort', onCallerAbort);
    }

    throwIfAborted(signal);
    if (failed) {
        throw new DownloadFailedError();
    }
    return {
        files: blobs.flatMap((blob, index) =>
            blob ? [new File([blob], instanceFileName(index))] : []
        ),
        skipped: instances.filter((_, index) => blobs[index] === null),
    };
}

/** PHI-free, request-ordered download file name. */
function instanceFileName(index: number): string {
    return `instance-${String(index + 1).padStart(6, '0')}.dcm`;
}

/**
 * One instance with 202-polling. Null means skippable; aborts and transport
 * failures propagate.
 */
async function downloadOne(
    address: InstanceAddress,
    signal: AbortSignal
): Promise<Blob | null> {
    try {
        return await withRetryOn202(() => fetchInstanceBlob(address, signal), {
            signal,
            timeoutMessage: 'Instance transfer did not complete before timeout',
        });
    } catch (error) {
        if (
            error instanceof InstanceNotFoundError ||
            error instanceof RetryTimeoutError ||
            error instanceof RequestDeadlineError
        ) {
            return null;
        }
        throw error;
    }
}

function assertDicomMediaType(response: Response): void {
    assertMediaType(response, DICOM_MEDIA_TYPES, 'instance');
}

function assertMediaType(
    response: Response,
    accepted: readonly string[],
    subject: string
): void {
    const contentType = response.headers.get('content-type') ?? '';
    const mediaType = contentType.split(';')[0].trim().toLowerCase();
    if (!accepted.includes(mediaType)) {
        throw new Error(
            `Unexpected ${subject} content type (${mediaType || 'none'})`
        );
    }
}

function instanceUrl(address: InstanceAddress): string {
    const study = encodeURIComponent(address.studyInstanceUid);
    const series = encodeURIComponent(address.seriesInstanceUid);
    const sop = encodeURIComponent(address.sopInstanceUid);
    return `/api/studies/${study}/series/${series}/instances/${sop}.dcm`;
}

function throwIfAborted(signal?: AbortSignal): void {
    if (signal?.aborted) {
        throw abortError();
    }
}
